// Bold is `__` not `**`: `**x**` is react-mentions mention markup and corrupts the value on insert.
const INLINE_WRAPPERS = {
  STRONG: '__',
  B: '__',
  EM: '*',
  I: '*',
  DEL: '~~',
  S: '~~',
  STRIKE: '~~',
};

const HEADINGS = { H1: '#', H2: '##', H3: '###', H4: '####', H5: '#####', H6: '######' };

const escapeText = (text) => text.replace(/([\\*`[\]])/g, '\\$1');

const collapse = (text) => text.replace(/[\t\r\n]+/g, ' ').replace(/ {2,}/g, ' ');

const isBlank = (text) => !text || !text.trim();

const isBoldWeight = (weight) =>
  weight === 'bold' || weight === 'bolder' || parseInt(weight, 10) >= 600;

// Google Docs wraps its whole paste in <b style="font-weight:normal">, so read the style not the tag.
const isRealBold = (el) => {
  const weight = el.style && el.style.fontWeight;
  return weight ? isBoldWeight(weight) : true;
};

// Docs and Word carry emphasis on span styles rather than <b>/<i>, so the tag list alone misses it.
const marksFromStyle = (el) => {
  const css = el.style;
  if (!css) return [];

  const marks = [];
  if (css.fontWeight && isBoldWeight(css.fontWeight)) marks.push('__');
  if (css.fontStyle === 'italic' || css.fontStyle === 'oblique') marks.push('*');
  if ((css.textDecoration || css.textDecorationLine || '').indexOf('line-through') !== -1) {
    marks.push('~~');
  }
  return marks;
};

const applyMarks = (text, marks) =>
  marks.reduce((acc, mark) => (acc ? `${mark}${acc}${mark}` : acc), text);

function serializeChildren(node, ctx) {
  let out = '';
  node.childNodes.forEach((child) => {
    out += serialize(child, ctx);
  });
  return out;
}

function serializeList(el, ctx) {
  const ordered = el.tagName === 'OL';
  const indent = '  '.repeat(ctx.depth);
  const childCtx = { ...ctx, depth: ctx.depth + 1 };
  let index = 0;

  const lines = [];
  el.childNodes.forEach((child) => {
    if (!child.tagName || child.tagName !== 'LI') return;
    index += 1;
    const marker = ordered ? `${index}. ` : '- ';
    const body = serializeChildren(child, childCtx).trim();
    if (body) lines.push(`${indent}${marker}${body.replace(/\n(?!\n)/g, `\n${indent}  `)}`);
  });

  return lines.join('\n');
}

function serializeElement(el, ctx) {
  const tag = el.tagName;

  if (tag === 'BR') return '\n';
  if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'HEAD') return '';

  if (tag === 'CODE' || tag === 'PRE') {
    const text = collapse(el.textContent || '').trim();
    if (!text) return '';
    return tag === 'PRE' ? `\n\n\`\`\`\n${el.textContent.trim()}\n\`\`\`\n\n` : `\`${text}\``;
  }

  if (INLINE_WRAPPERS[tag]) {
    if ((tag === 'B' || tag === 'STRONG') && !isRealBold(el)) return serializeChildren(el, ctx);
    const inner = serializeChildren(el, ctx).trim();
    if (!inner) return '';
    const mark = INLINE_WRAPPERS[tag];
    return `${mark}${inner}${mark}`;
  }

  if (tag === 'A') {
    const href = el.getAttribute('href');
    const inner = serializeChildren(el, ctx).trim();
    if (!inner) return '';
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) return inner;
    return href === inner ? inner : `[${inner}](${href})`;
  }

  if (HEADINGS[tag]) {
    const inner = serializeChildren(el, ctx).trim();
    return inner ? `\n\n${HEADINGS[tag]} ${inner}\n\n` : '';
  }

  if (tag === 'UL' || tag === 'OL') {
    const list = serializeList(el, ctx);
    return list ? `\n\n${list}\n\n` : '';
  }

  if (tag === 'BLOCKQUOTE') {
    const inner = serializeChildren(el, ctx).trim();
    if (!inner) return '';
    return `\n\n${inner.split('\n').map((line) => `> ${line}`).join('\n')}\n\n`;
  }

  if (tag === 'P' || tag === 'DIV' || tag === 'SECTION' || tag === 'ARTICLE' || tag === 'TR') {
    const inner = applyMarks(serializeChildren(el, ctx).trim(), marksFromStyle(el));
    return inner ? `\n\n${inner}\n\n` : '';
  }

  const inner = serializeChildren(el, ctx);
  const marks = marksFromStyle(el);
  return marks.length ? applyMarks(inner.trim(), marks) : inner;
}

function serialize(node, ctx) {
  if (node.nodeType === 3) return escapeText(collapse(node.nodeValue || ''));
  if (node.nodeType !== 1) return '';
  return serializeElement(node, ctx);
}

export default function htmlToMarkdown(html) {
  if (isBlank(html)) return '';

  let doc;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch (e) {
    return '';
  }
  if (!doc || !doc.body) return '';

  return serializeChildren(doc.body, { depth: 0 })
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
